import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Input } from '../ui/Input';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend, LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

// The "Workouts" view: logged exercise SESSIONS (cardio/strength/flexibility),
// distinct from the Activity tab's passive tracker movement (steps, daily
// active minutes). Fitness is about consistency (did you show up), mix, and — for
// strength — progression (lifting more over time). This is a descriptive lever,
// never a graded forecast (workout modality is sensor-blind by doctrine).

import { RangeSelector, useTimeRange } from './TimeRange';

const TYPE_META = {
  'exercise.cardio': { key: 'cardio', label: 'Cardio', color: '#FFB800' },
  'exercise.strength': { key: 'strength', label: 'Strength', color: '#10B981' },
  'exercise.flexibility': { key: 'flexibility', label: 'Flexibility', color: '#5FA8C7' },
};
const WORKOUT_SLUGS = Object.keys(TYPE_META);

// Weekly workout-frequency target. No home in the (daily-band) MetricTarget
// system, so it lives on PersonalStats.weekly_workout_target — a real
// cross-device user setting, seeded with a sensible default.
const DEFAULT_TARGET = 3;

// Monday-based week-start (YYYY-MM-DD) for grouping.
const weekStartOf = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = (d.getDay() + 6) % 7; // Mon = 0
  d.setDate(d.getDate() - dow);
  return d.toISOString().split('T')[0];
};

const addDaysIso = (dateStr, n) => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

const weekLabel = (weekStr) =>
  new Date(weekStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const FitnessDataView = () => {
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { inRange, selectorProps } = useTimeRange();
  const [target, setTarget] = useState(DEFAULT_TARGET);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [selectedLift, setSelectedLift] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, statsRes] = await Promise.all([
        apiClient.get('/api/health/entries', { params: { category: 'exercise', limit: 1000 } }),
        apiClient.get('/api/personal-stats/').catch(() => null), // 404 for a brand-new user
      ]);
      // Keep only logged workout sessions (the exercise.* subcategories) — drop
      // the passive daily_activity aggregate that the Activity tab already owns.
      setAllWorkouts((res.data || []).filter((e) => WORKOUT_SLUGS.includes(e.category_slug)));
      const t = statsRes?.data?.weekly_workout_target;
      if (Number.isFinite(t) && t > 0) setTarget(t);
    } catch (error) {
      console.error('Error fetching workouts:', error);
      toast.error('Failed to load workouts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const workouts = useMemo(
    () => allWorkouts.filter((e) => inRange(e.entry_date)),
    [allWorkouts, inRange],
  );

  // Weekly buckets, zero-filled across the span so gaps read as missed weeks.
  const weeklyData = useMemo(() => {
    if (!workouts.length) return [];
    const byWeek = {};
    workouts.forEach((e) => {
      const wk = weekStartOf(e.entry_date);
      if (!byWeek[wk]) byWeek[wk] = { week: wk, cardio: 0, strength: 0, flexibility: 0, total: 0 };
      const meta = TYPE_META[e.category_slug];
      byWeek[wk][meta.key] += 1;
      byWeek[wk].total += 1;
    });
    const weeks = Object.keys(byWeek).sort();
    const first = weeks[0];
    const lastData = weeks[weeks.length - 1];
    const today = new Date().toISOString().split('T')[0];
    const last = weekStartOf(today) > lastData ? weekStartOf(today) : lastData;
    const out = [];
    for (let w = first; w <= last; w = addDaysIso(w, 7)) {
      out.push(byWeek[w] || { week: w, cardio: 0, strength: 0, flexibility: 0, total: 0 });
    }
    return out;
  }, [workouts]);

  const stats = useMemo(() => {
    if (!workouts.length) return null;
    const total = workouts.length;
    const totalMin = workouts.reduce((s, e) => s + (e.duration_minutes || 0), 0);
    const weeks = weeklyData.length || 1;
    const byType = {};
    workouts.forEach((e) => {
      const meta = TYPE_META[e.category_slug];
      byType[meta.key] = (byType[meta.key] || 0) + 1;
    });
    const topType = Object.entries(byType).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
    return {
      total,
      perWeek: (total / weeks).toFixed(1),
      hours: (totalMin / 60).toFixed(1),
      topType: topType.charAt(0).toUpperCase() + topType.slice(1),
    };
  }, [workouts, weeklyData]);

  const donutData = useMemo(() => {
    const byType = {};
    workouts.forEach((e) => {
      const meta = TYPE_META[e.category_slug];
      byType[meta.key] = (byType[meta.key] || 0) + 1;
    });
    const total = Object.values(byType).reduce((s, v) => s + v, 0);
    if (!total) return [];
    return Object.entries(TYPE_META)
      .map(([, m]) => ({ name: m.label, value: byType[m.key] || 0, color: m.color }))
      .filter((d) => d.value > 0)
      .map((d) => ({ ...d, pct: Math.round((d.value / total) * 100) }));
  }, [workouts]);

  // Strength progression — max working weight per exercise over time, from the
  // data.exercises[] array on strength sessions.
  const liftData = useMemo(() => {
    const byLift = {}; // name -> Map(date -> maxWeight)
    workouts
      .filter((e) => e.category_slug === 'exercise.strength' && Array.isArray(e.data?.exercises))
      .forEach((e) => {
        e.data.exercises.forEach((ex) => {
          const name = (ex.name || '').trim();
          const wt = Number(ex.weight_lbs ?? ex.weight);
          if (!name || !Number.isFinite(wt)) return;
          if (!byLift[name]) byLift[name] = new Map();
          const prev = byLift[name].get(e.entry_date);
          if (prev == null || wt > prev) byLift[name].set(e.entry_date, wt);
        });
      });
    const lifts = {};
    Object.entries(byLift).forEach(([name, m]) => {
      lifts[name] = [...m.entries()]
        .sort((a, b) => (a[0] < b[0] ? -1 : 1))
        .map(([date, weight]) => ({ date, weight }));
    });
    return lifts;
  }, [workouts]);

  const liftNames = useMemo(
    () => Object.keys(liftData).sort((a, b) => liftData[b].length - liftData[a].length),
    [liftData]
  );

  // Default the lift picker to the most-logged lift once data arrives.
  useEffect(() => {
    if (liftNames.length && !liftNames.includes(selectedLift)) setSelectedLift(liftNames[0]);
  }, [liftNames, selectedLift]);

  const saveTarget = async () => {
    const v = parseFloat(targetInput);
    if (!Number.isFinite(v) || v <= 0) {
      setEditingTarget(false);
      return;
    }
    try {
      try {
        await apiClient.put('/api/personal-stats/', { weekly_workout_target: v });
      } catch (err) {
        if (err?.response?.status === 404) {
          // No stats row yet → create one carrying just this field.
          await apiClient.post('/api/personal-stats/', { weekly_workout_target: v });
        } else {
          throw err;
        }
      }
      setTarget(v);
      setEditingTarget(false);
      toast.success('Weekly workout target updated');
    } catch (error) {
      toast.error('Failed to update workout target');
    }
  };

  const WeeklyTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div className="bg-raised border border-line rounded-lg p-3 text-sm">
        <p className="font-medium text-fg mb-2">Week of {weekLabel(d.week)}</p>
        <div className="space-y-1">
          {Object.values(TYPE_META).map((m) =>
            d[m.key] > 0 ? (
              <p key={m.key} style={{ color: m.color }}>
                {m.label}: <span className="font-medium">{d[m.key]}</span>
              </p>
            ) : null
          )}
          <p className="border-t border-line  pt-1 mt-1 font-medium text-fg">
            Total: {d.total} workout{d.total === 1 ? '' : 's'}
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fg"></div>
      </div>
    );
  }

  if (!allWorkouts.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-fg-2 font-medium mb-2">
            No workouts logged yet
          </p>
          <p className="text-center text-sm text-fg-2 max-w-md mx-auto">
            Log a session in plain language — say or type{' '}
            <span className="font-medium text-fg">
              "ran 3 miles"
            </span>{' '}
            or{' '}
            <span className="font-medium text-fg">
              "bench press 3×8 at 135 lbs"
            </span>{' '}
            — and your consistency, mix, and strength progression show up here.
            (Passive steps &amp; active minutes live on the Activity tab.)
          </p>
        </CardContent>
      </Card>
    );
  }

  const tickInterval = Math.max(0, Math.ceil(weeklyData.length / 10) - 1);
  const selectedSeries = selectedLift ? liftData[selectedLift] : null;

  return (
    <div className="space-y-6">
      {/* Range selector */}
      <RangeSelector
        {...selectorProps}
        right={(
          <span className="shrink-0 text-sm text-fg-2">
            {workouts.length} workout{workouts.length === 1 ? '' : 's'} in range
          </span>
        )}
      />

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Per Week</p>
            <p className="text-2xl font-medium text-fg">{stats?.perWeek ?? '--'}</p>
            <p className={`text-sm mt-1 ${parseFloat(stats?.perWeek) >= target ? 'text-good-strong dark:text-good' : 'text-fg-3'}`}>
              {parseFloat(stats?.perWeek) >= target ? 'At/above' : 'Below'} target ({target}/wk)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Workouts</p>
            <p className="text-2xl font-medium text-fg">{stats?.total ?? '--'}</p>
            <p className="text-sm text-fg-2 mt-1">in range</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Active Time</p>
            <p className="text-2xl font-medium text-fg">{stats?.hours ?? '--'}</p>
            <p className="text-sm text-fg-2 mt-1">hours</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Most Common</p>
            <p className="text-2xl font-medium text-fg">{stats?.topType ?? '--'}</p>
            <p className="text-sm text-fg-2 mt-1">by count</p>
          </CardContent>
        </Card>
      </div>

      {/* Weekly target inline editor */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-fg-2">Weekly workout target:</span>
        {editingTarget ? (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={targetInput}
              onChange={(e) => setTargetInput(e.target.value)}
              className="w-24"
              min="1"
              step="1"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveTarget();
                if (e.key === 'Escape') setEditingTarget(false);
              }}
            />
            <button onClick={saveTarget} className="p-1 text-good-strong hover:text-green-700">
              <CheckIcon className="w-5 h-5" />
            </button>
            <button onClick={() => setEditingTarget(false)} className="p-1 text-fg-3 hover:text-fg">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => { setTargetInput(String(target)); setEditingTarget(true); }}
            className="flex items-center gap-1.5 text-sm font-medium text-fg-2 hover:text-fg transition-colors"
          >
            {target}/week
            <PencilIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Workouts per week */}
      {weeklyData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Workouts per week</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={weeklyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                <XAxis
                  dataKey="week"
                  tickFormatter={weekLabel}
                  interval={tickInterval}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  width={32}
                />
                <Tooltip content={<WeeklyTooltip />} />
                <Legend wrapperStyle={{ fontSize: 14 }} />
                <ReferenceLine
                  y={target}
                  stroke="#10B981"
                  strokeDasharray="5 3"
                  label={{ value: `Target ${target}`, position: 'insideTopLeft', fill: '#10B981', fontSize: 14 }}
                />
                <Bar dataKey="cardio" stackId="w" fill={TYPE_META['exercise.cardio'].color} name="Cardio" />
                <Bar dataKey="strength" stackId="w" fill={TYPE_META['exercise.strength'].color} name="Strength" />
                <Bar dataKey="flexibility" stackId="w" fill={TYPE_META['exercise.flexibility'].color} name="Flexibility" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Type breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Workout mix</CardTitle>
          </CardHeader>
          <CardContent>
            {donutData.length > 0 ? (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">
                      {donutData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) =>
                        active && payload?.length ? (
                          <div className="bg-raised border border-line rounded-lg p-3 text-sm">
                            <p style={{ color: payload[0].payload.color }} className="font-medium">
                              {payload[0].payload.name}: {payload[0].payload.value} ({payload[0].payload.pct}%)
                            </p>
                          </div>
                        ) : null
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-4 text-sm mt-2 justify-center">
                  {donutData.map((d) => (
                    <div key={d.name} className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: d.color }} />
                      <span className="text-fg-3">{d.name} {d.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-fg-2 py-8">No workout data in range</p>
            )}
          </CardContent>
        </Card>

        {/* Strength progression */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Strength progression</CardTitle>
          </CardHeader>
          <CardContent>
            {liftNames.length > 0 && selectedSeries ? (
              <>
                <select
                  value={selectedLift}
                  onChange={(e) => setSelectedLift(e.target.value)}
                  className="mb-3 h-10 rounded-md border border-line-strong bg-raised  px-3 py-2 text-sm text-fg"
                >
                  {liftNames.map((n) => (
                    <option key={n} value={n}>{n.charAt(0).toUpperCase() + n.slice(1)}</option>
                  ))}
                </select>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={selectedSeries} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      tick={{ fontSize: 14, fill: '#9ca3af' }}
                      axisLine={{ stroke: '#4b5563' }}
                      tickLine={{ stroke: '#4b5563' }}
                      minTickGap={30}
                    />
                    <YAxis
                      tick={{ fontSize: 14, fill: '#9ca3af' }}
                      axisLine={{ stroke: '#4b5563' }}
                      tickLine={{ stroke: '#4b5563' }}
                      width={40}
                      label={{ value: 'lbs', angle: -90, position: 'insideLeft', style: { fill: '#9ca3af', fontSize: 14 } }}
                    />
                    <Tooltip
                      content={({ active, payload }) =>
                        active && payload?.length ? (
                          <div className="bg-raised border border-line rounded-lg p-3 text-sm">
                            <p className="font-medium text-fg">
                              {new Date(payload[0].payload.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                            <p style={{ color: TYPE_META['exercise.strength'].color }} className="font-medium">
                              {payload[0].payload.weight} lbs
                            </p>
                          </div>
                        ) : null
                      }
                    />
                    <Line
                      dataKey="weight"
                      stroke={TYPE_META['exercise.strength'].color}
                      strokeWidth={2.5}
                      dot={selectedSeries.length <= 30 ? { r: 3, fill: TYPE_META['exercise.strength'].color } : false}
                      type="monotone"
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            ) : (
              <p className="text-center text-fg-2 py-8">
                Log strength sets with weights (e.g. "squat 5×5 at 185 lbs") to track progression here.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FitnessDataView;
